import { featureViewQueryKeys } from "@/app/data/featureViewQueryKeys";
import {
  FeatureListQuery,
  FeatureNamesQueryType,
} from "@reflag/shared/featureAPI";

const qk = {
  list: (
    appId?: string,
    envId?: string,
    params?: Omit<FeatureListQuery, "envId">,
  ) =>
    [
      "apps",
      appId,
      "environments",
      envId,
      "features",
      ...(params ? [params] : []),
    ] as const,

  listNames: (appId?: string, params: FeatureNamesQueryType = {}) =>
    ["apps", appId, "names", params] as const,

  single: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.list(appId, envId), featureId] as const,

  singleMetrics: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "metrics"] as const,

  singleMetricsHistorical: (
    appId?: string,
    envId?: string,
    featureId?: string,
    params?: Record<string, any>,
  ) =>
    [
      ...qk.singleMetrics(appId, envId, featureId),
      "historical",
      params,
    ] as const,

  singleFeatureFeedbackHistogram: (
    appId?: string,
    envId?: string,
    featureId?: string,
    params?: Record<string, any>,
  ) =>
    [
      ...qk.single(appId, envId, featureId),
      "feedback",
      "histogram",
      params,
    ] as const,

  singleFeatureFeedbackCampaign: (
    appId?: string,
    envId?: string,
    featureId?: string,
  ) =>
    [
      ...qk.single(appId, envId, featureId),
      "featureFeedbackCampaigns",
    ] as const,

  keys: (appId?: string) => [appId, "featureKeys"] as const,

  singleFeatureRolloutStatus: (
    appId?: string,
    envId?: string,
    featureId?: string,
  ) => [...qk.single(appId, envId, featureId), "rollout"] as const,

  singleFeatureCompanies: (
    appId?: string,
    envId?: string,
    featureId?: string,
    params?: Record<string, any>,
  ) => [...qk.single(appId, envId, featureId), "companies", params] as const,

  singleFeatureCompanyUsers: (
    appId?: string,
    envId?: string,
    featureId?: string,
    companyId?: string,
  ) => [
    ...qk.singleFeatureCompanies(appId, envId, featureId),
    companyId,
    "users",
  ],

  featureViews: (appId?: string, featureId?: string) =>
    [
      ...featureViewQueryKeys.singleFeatureRelationViews(appId),
      featureId,
    ] as const,

  config: (appId?: string, featureId?: string) =>
    [...qk.single(appId, undefined, featureId), "config"] as const,

  configVersions: (appId?: string, featureId?: string, params?: object) =>
    [
      ...qk.single(appId, undefined, featureId),
      "config",
      "versions",
      ...(params ? [params] : []),
    ] as const,

  targeting: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "targeting"] as const,

  optInMemberships: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "opt-in-memberships"] as const,

  targetingVersions: (
    appId: string,
    envId: string,
    featureId: string,
    params?: Record<string, any>,
  ) =>
    params
      ? ([
          ...qk.targeting(appId, envId, featureId),
          "versions",
          params,
        ] as const)
      : ([...qk.targeting(appId, envId, featureId), "versions"] as const),

  targetingContextAttributeKeys: (
    appId?: string,
    envId?: string,
    prefix?: string,
    filter?: string,
  ) => [...qk.list(appId, envId), "context", "keys", prefix, filter] as const,

  targetingContextAttributeValues: (
    appId?: string,
    envId?: string,
    prefix?: string,
    value?: string,
    filter?: string,
  ) =>
    [
      ...qk.targetingContextAttributeKeys(appId, envId, prefix),
      "values",
      value,
      filter,
    ] as const,

  pullRequests: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "pull-requests"] as const,

  linearCleanupIssues: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "linear-cleanup-issues"] as const,

  archivingChecks: (appId?: string, envId?: string, featureId?: string) =>
    [...qk.single(appId, envId, featureId), "archiving-checks"] as const,

  singleAdoption: (
    appId?: string,
    envId?: string,
    featureId?: string,
    params?: Record<string, any>,
  ) => [...qk.single(appId, envId, featureId), "adoption", params] as const,

  flags: (appId?: string, params?: unknown) =>
    ["apps", appId, "flags", params] as const,
};

export { qk as featureQueryKeys };
